import AppService from 'App/legacy/AppService';
import { hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import type Appointment from 'App/resources/Model/Appointment';
import { visitTypes } from './visitTypes';

export type CheckInFields = {
	checkInLatitude: number;
	checkInLongitude: number;
	checkInAccuracyM: number | null;
	checkInCapturedAt: string;
	checkInSource: 'browser';
	checkInDistanceM: null;
	checkInUserId: null;
};

const POSITION_OPTIONS: PositionOptions = { enableHighAccuracy: true, maximumAge: 60_000, timeout: 10_000 };
const GIVE_UP_AFTER = 15_000;

/** Null when the browser refuses, has none or does not answer in time - the visit is saved
 * without a check-in rather than held up or failed. */
export function checkInPosition(): Promise<CheckInFields | null> {
	return new Promise(resolve => {
		if (!navigator.geolocation) {
			resolve(null);
			return;
		}
		// `timeout` doesn't count the time a permission prompt sits unanswered.
		const giveUp = setTimeout(() => resolve(null), GIVE_UP_AFTER);
		navigator.geolocation.getCurrentPosition(
			({ coords, timestamp }) => {
				clearTimeout(giveUp);
				resolve({
					checkInLatitude: coords.latitude,
					checkInLongitude: coords.longitude,
					checkInAccuracyM: Number.isFinite(coords.accuracy) ? coords.accuracy : null,
					checkInCapturedAt: new Date(timestamp).toISOString(),
					checkInSource: 'browser',
					// Spread over a loaded appointment, these blank its old server-owned keys: the API drops a
					// point sent back with them as a read.
					checkInDistanceM: null,
					checkInUserId: null
				});
			},
			() => {
				clearTimeout(giveUp);
				resolve(null);
			},
			POSITION_OPTIONS
		);
	});
}

function asksForCheckIn(activityTypeId: number | undefined, from: string | null | undefined, to: unknown): boolean {
	return (
		to === 'completed' &&
		from !== 'completed' &&
		activityTypeId !== undefined &&
		hasSoftDeployAccess('FIELD_SALES_V2') &&
		visitTypes(AppService.getActivityTypes('appointment')).some(type => type.id === activityTypeId)
	);
}

/** Asks the browser only on the save that marks a visit done; null for every other save. */
export function visitCheckIn(
	activityTypeId: number | undefined,
	from: string | null | undefined,
	to: unknown
): Promise<CheckInFields | null> {
	return asksForCheckIn(activityTypeId, from, to) ? checkInPosition() : Promise.resolve(null);
}

export const pickCheckIn = ({
	checkInLatitude,
	checkInLongitude,
	checkInAccuracyM,
	checkInCapturedAt,
	checkInSource,
	checkInDistanceM,
	checkInUserId
}: Partial<Appointment>) => ({
	checkInLatitude,
	checkInLongitude,
	checkInAccuracyM,
	checkInCapturedAt,
	checkInSource,
	checkInDistanceM,
	checkInUserId
});
